/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import { BlogTagFragment, CosmosAssetFragment } from "@cosmos/hygraph-schema";
import { useEffect, useRef, useState } from "react";

import { CosmosTagsAndAttribution, CosmosImage, CosmosTagProps } from "@components/elements";
import { css, cx } from "@panda/css";

export type CosmosVideoProps = {
	video?: CosmosAssetFragment | null;
	tags?: CosmosTagProps[] | BlogTagFragment[] | null;
	className?: string;
	attributionClassName?: string;
	disableAutoplay?: boolean;
	hideAttribution?: boolean;
};

export const CosmosVideo = ({
	className,
	video,
	tags,
	disableAutoplay,
	hideAttribution,
	attributionClassName,
}: CosmosVideoProps): React.JSX.Element | null => {
	const { url, mimeType, fallbackImage, attribution: videoAttribution } = video as CosmosAssetFragment;
	const attribution = !hideAttribution && !!videoAttribution ? videoAttribution : undefined;

	const [showImg, setShowImg] = useState(Boolean(!url || !mimeType?.includes("video")));

	const videoRef = useRef<HTMLSourceElement>(null);
	const videoSrcAttrs = mimeType ? { type: mimeType } : {};

	useEffect(() => {
		if (videoRef.current) {
			const videoElem = videoRef.current;
			videoElem.addEventListener("error", () => setShowImg(true));
		}
	}, [videoRef]);

	const fallbackImageEl = (
		<CosmosImage
			className={cx("cosmos-video-fallback-image", className, css({ objectFit: "cover", w: "full" }))}
			src={fallbackImage?.optUrl || ""}
			width={fallbackImage?.width || 0}
			height={fallbackImage?.height || 0}
			alt={fallbackImage?.altText || ""}
		/>
	);

	const fallbackWithAttr = attribution ? (
		<CosmosTagsAndAttribution tags={tags} attribution={attribution} className={attributionClassName}>
			{fallbackImageEl}
		</CosmosTagsAndAttribution>
	) : (
		fallbackImageEl
	);

	return (
		<>
			{showImg ? (
				!fallbackImage ? undefined : (
					fallbackWithAttr
				)
			) : (
				<CosmosTagsAndAttribution tags={tags} attribution={attribution} className={attributionClassName}>
					<video
						key={`cosmos-video-${url}`}
						className={cx("cosmos-video", className, css({ objectFit: "cover", aspectRatio: "16 / 9" }))}
						title={video?.altText || ""}
						controls={!!video?.enableVideoControls}
						playsInline
						disableRemotePlayback
						autoPlay={!disableAutoplay}
						muted
						loop
						tabIndex={-1}
						poster={fallbackImage?.optUrl}
					>
						<source ref={videoRef} src={url} {...videoSrcAttrs} />
						{fallbackImage?.optUrl && fallbackImage?.mimeType ? fallbackImageEl : null}
					</video>
				</CosmosTagsAndAttribution>
			)}
		</>
	);
};
